// Danger zone: self-service account deletion (issue #136 — Law 25 / PIPEDA erasure).
//
// Three-step flow on purpose. The user must SEE what will happen before they can act:
//   1. preview   — server reports what would be deleted, kept, and what blocks it
//   2. disclose  — that list is shown verbatim; blockers replace the button entirely
//   3. confirm   — password (+ MFA) and the account email typed back
//
// Deletion is immediate and irreversible, so nothing here is a single click, and the
// blockers are surfaced as instructions rather than errors after the fact.
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import apiClient from "api/apiClient";
import { darkFieldSx } from "utils/darkFieldSx";

const API = process.env.REACT_APP_BASE_URL;

const DeleteAccountPanel = () => {
  const user = useSelector((state) => state.auth.user);
  const email = user?.email || "";

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreviewError("");
    try {
      const { data } = await apiClient.post(
        `${API}/user/account-deletion/preview`,
        {},
        { withCredentials: true },
      );
      setPreview(data);
    } catch (err) {
      setPreviewError(
        err?.response?.data?.message ||
          "Could not check what deleting your account would remove.",
      );
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  // Re-checked every time the dialog opens: a run may have started, or an ownership
  // transfer completed, since the page was loaded.
  useEffect(() => {
    if (open) loadPreview();
  }, [open, loadPreview]);

  const emailMatches =
    typedEmail.trim().toLowerCase() === email.trim().toLowerCase() && email !== "";
  const blocked = Boolean(preview && !preview.canDelete);
  const canSubmit =
    !submitting && !loadingPreview && !blocked && emailMatches && password.length > 0;

  const handleDelete = async () => {
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post(
        `${API}/user/account-deletion`,
        { password, mfaToken: mfaToken || undefined, confirmationText: typedEmail.trim() },
        { withCredentials: true },
      );
      // The server has already revoked the session and cleared the cookies. A hard
      // navigation drops all in-memory auth state rather than leaving the SPA holding a
      // user object for an account that no longer exists.
      window.location.href = "/";
    } catch (err) {
      const data = err?.response?.data;
      if (data?.blockers?.length) {
        setPreview((p) => ({ ...(p || {}), canDelete: false, blockers: data.blockers }));
        setError("Your account cannot be deleted yet — see below.");
      } else {
        setError(data?.message || "Could not delete your account. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setPassword("");
    setMfaToken("");
    setTypedEmail("");
    setError("");
  };

  return (
    <div className="settings-section">
      <h3 style={{ color: "#b42318" }}>Delete account</h3>
      <p>
        Permanently delete your account and your personal projects. Any subscription is
        cancelled with no refund, and remaining credits are lost. This happens immediately and
        cannot be undone.
      </p>
      <Button variant="outlined" color="error" onClick={() => setOpen(true)}>
        Delete my account
      </Button>

      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#b42318" }}>Delete your account?</DialogTitle>
        <DialogContent dividers>
          {loadingPreview && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Checking what would be removed…</Typography>
            </Box>
          )}

          {previewError && <Alert severity="error">{previewError}</Alert>}

          {preview && !loadingPreview && (
            <>
              {/* Blockers replace the form entirely — there is nothing useful to fill in
                  until they are resolved, and each says what to do about it. */}
              {blocked ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Your account cannot be deleted yet.
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {preview.blockers.map((b) => (
                      <li key={b.code + (b.organizationId || "")}>
                        <Typography variant="body2">{b.message}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              ) : (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      This is immediate and cannot be undone.
                    </Typography>
                  </Alert>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    What will be deleted
                  </Typography>
                  <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                    <li>
                      <Typography variant="body2">
                        {preview.willDelete.personalProjectCount} personal project
                        {preview.willDelete.personalProjectCount === 1 ? "" : "s"} and all of
                        {preview.willDelete.personalProjectCount === 1 ? " its" : " their"}{" "}
                        scenarios and results
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Your sign-in credentials, sessions and trusted devices
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Your single sign-on link, if any</Typography>
                    </li>
                  </ul>

                  {/* What the user gives up (billing review P5.3): the dialog used to say
                      nothing about the subscription or the balances. */}
                  {(preview.willForfeit?.cancelsSubscription ||
                    preview.willForfeit?.planCredits > 0 ||
                    preview.willForfeit?.credits > 0) && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                        What you give up
                      </Typography>
                      <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                        {preview.willForfeit.cancelsSubscription && (
                          <li>
                            <Typography variant="body2">
                              Your {preview.willForfeit.subscriptionPlan ? `${preview.willForfeit.subscriptionPlan} ` : ""}
                              subscription is cancelled immediately, with no refund for the rest of the
                              billing period
                            </Typography>
                          </li>
                        )}
                        {(preview.willForfeit.planCredits > 0 || preview.willForfeit.credits > 0) && (
                          <li>
                            <Typography variant="body2">
                              Your remaining credits
                              {preview.willForfeit.planCredits > 0 ? ` (${preview.willForfeit.planCredits} plan` : " ("}
                              {preview.willForfeit.planCredits > 0 && preview.willForfeit.credits > 0 ? ", " : ""}
                              {preview.willForfeit.credits > 0 ? `${preview.willForfeit.credits} simulation` : ""}
                              {" credits)"} are lost; they cannot be refunded or transferred
                            </Typography>
                          </li>
                        )}
                      </ul>
                    </>
                  )}

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                    What will be kept
                  </Typography>
                  <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                    <li>
                      <Typography variant="body2">
                        Projects owned by an organization — they belong to the organization,
                        not to you
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Billing records, which we retain as a financial audit trail
                      </Typography>
                    </li>
                  </ul>

                  {preview.organizations?.length > 0 && (
                    // The dialog sits on the app's dark surface, so a slate meant for a
                    // light background is all but invisible here.
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "rgba(223, 229, 255, 0.84)" }}
                    >
                      You will be removed from{" "}
                      {preview.organizations.map((o) => o.name).join(", ")}.
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      size="small"
                      type="password"
                      label="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      fullWidth
                      sx={darkFieldSx}
                    />
                    {user?.mfaEnabled && (
                      <TextField
                        size="small"
                        label="Authenticator code"
                        value={mfaToken}
                        onChange={(e) => setMfaToken(e.target.value)}
                        inputProps={{ maxLength: 6, inputMode: "numeric" }}
                        fullWidth
                        sx={darkFieldSx}
                      />
                    )}
                    <TextField
                      size="small"
                      label={`Type ${email} to confirm`}
                      value={typedEmail}
                      onChange={(e) => setTypedEmail(e.target.value)}
                      error={typedEmail.length > 0 && !emailMatches}
                      helperText={
                        typedEmail.length > 0 && !emailMatches
                          ? "This does not match your account email."
                          : " "
                      }
                      fullWidth
                      sx={darkFieldSx}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {/* Outlined and light rather than the theme's default text-primary blue, which is
              close to invisible on this dark surface. Cancel is the safe way out of a
              destructive dialog, so it has to be the easiest control to find. */}
          <Button
            onClick={close}
            disabled={submitting}
            variant="outlined"
            sx={{
              color: "rgba(233, 238, 255, 0.94)",
              borderColor: "rgba(255, 255, 255, 0.34)",
              fontWeight: 600,
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.6)",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
              "&.Mui-disabled": {
                color: "rgba(233, 238, 255, 0.45)",
                borderColor: "rgba(255, 255, 255, 0.16)",
              },
            }}
          >
            Cancel
          </Button>
          {!blocked && (
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={!canSubmit}
            >
              {submitting ? "Deleting…" : "Delete my account permanently"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeleteAccountPanel;
